import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import GatewaySession from './GatewaySession';
import './CommandCenter.css';

const API_BASE_URL = '/api';

const CommandCenter = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    pendingProcessing: 0,
    processedVaults: 0,
    currentMonthRevenue: 0,
    allTimeRevenue: 0,
    attentionNeeded: 0
  });
  const [revenueFilter, setRevenueFilter] = useState('month');

  // Applications table state (merged from StatusCenter)
  const [applications, setApplications] = useState([]);
  const [appLoading, setAppLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    visaType: '',
    country: '',
    page: 1,
    limit: 10
  });
  const [filterData, setFilterData] = useState({ visaTypes: [], countries: [] });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  // Dropdown states
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(null);
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [visaTypeDropdownOpen, setVisaTypeDropdownOpen] = useState(false);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const filterDropdownRef = useRef(null);
  const visaTypeDropdownRef = useRef(null);
  const countryDropdownRef = useRef(null);
  const statusDropdownRefs = useRef({});

  // Visa upload state
  const [visaUploadAppId, setVisaUploadAppId] = useState(null);
  const [visaFile, setVisaFile] = useState(null);
  const [uploadingVisa, setUploadingVisa] = useState(false);
  const [visaUploadSuccess, setVisaUploadSuccess] = useState(false);

  // Gateway drawer state
  const [selectedAppId, setSelectedAppId] = useState(null);
  const prevSelectedAppId = useRef(null);

  // Status change confirmation modal state
  const [statusConfirm, setStatusConfirm] = useState(null); // { appId, newStatus, currentStatus, appNumber, applicantName }

  // WhatsApp confirmation modal state
  const [whatsAppConfirm, setWhatsAppConfirm] = useState(null); // { appId, newStatus, applicantName }

  // Fetch command center stats
  const fetchStats = useCallback(async () => {
    try {
      const statsRes = await fetch(`${API_BASE_URL}/command-center/stats`, {
        credentials: 'include'
      });
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        if (statsData.success) {
          setStats(statsData.data.stats);
        }
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch applications (from status-center API)
  const fetchApplications = useCallback(async () => {
    try {
      setAppLoading(true);
      const queryParams = new URLSearchParams();
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.search) queryParams.append('search', filters.search);
      if (filters.visaType) queryParams.append('visaType', filters.visaType);
      if (filters.country) queryParams.append('country', filters.country);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);

      const response = await fetch(`${API_BASE_URL}/status-center/applications?${queryParams.toString()}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data.applications) {
          setApplications(data.data.applications);
          setPagination(data.data.pagination);
        }
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setAppLoading(false);
    }
  }, [filters]);

  // Fetch filter options (visa types + countries)
  useEffect(() => {
    fetch(`${API_BASE_URL}/status-center/filters`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        if (data.success) setFilterData(data.data);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  // Silent refresh when drawer closes (no loading spinner)
  useEffect(() => {
    if (prevSelectedAppId.current && !selectedAppId) {
      // Drawer just closed — refresh data silently in background
      fetchStats();
      const queryParams = new URLSearchParams();
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.search) queryParams.append('search', filters.search);
      if (filters.visaType) queryParams.append('visaType', filters.visaType);
      if (filters.country) queryParams.append('country', filters.country);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);
      fetch(`${API_BASE_URL}/status-center/applications?${queryParams.toString()}`, { credentials: 'include' })
        .then(res => res.json())
        .then(data => {
          if (data.success && data.data.applications) {
            setApplications(data.data.applications);
            setPagination(data.data.pagination);
          }
        })
        .catch(() => {});
    }
    prevSelectedAppId.current = selectedAppId;
  }, [selectedAppId, filters, fetchStats]);

  // Click outside handler for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setFilterDropdownOpen(false);
      }
      if (visaTypeDropdownRef.current && !visaTypeDropdownRef.current.contains(event.target)) {
        setVisaTypeDropdownOpen(false);
      }
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
        setCountryDropdownOpen(false);
      }
      if (statusDropdownOpen !== null) {
        const currentRef = statusDropdownRefs.current[statusDropdownOpen];
        if (currentRef && !currentRef.contains(event.target)) {
          setStatusDropdownOpen(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [statusDropdownOpen]);

  const handleSearch = (e) => {
    setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }));
  };

  const handleStatusFilter = (status) => {
    setFilters(prev => ({ ...prev, status: prev.status === status ? '' : status, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setFilters(prev => ({ ...prev, page: newPage }));
    }
  };

  // Step 1: Dropdown click → show status confirmation
  const requestStatusChange = (id, newStatus) => {
    const app = applications.find(a => a.id === id);
    if (app.status === newStatus) return;
    setStatusConfirm({
      appId: id,
      newStatus,
      currentStatus: app.status,
      appNumber: app.applicationNumber || app.application_number || `#${id}`,
      applicantName: app?.applicant?.name || 'Applicant'
    });
    setStatusDropdownOpen(null);
  };

  // Step 2: Status confirmed → if approved/rejected show WhatsApp modal, else execute
  const confirmStatusChange = () => {
    const { appId, newStatus, applicantName } = statusConfirm;
    setStatusConfirm(null);
    if (newStatus === 'approved' || newStatus === 'rejected') {
      setWhatsAppConfirm({ appId, newStatus, applicantName });
    } else {
      executeStatusChange(appId, newStatus, false);
    }
  };

  // Step 3: Execute the actual API call
  const executeStatusChange = async (id, newStatus, sendWhatsApp = false) => {
    try {
      const response = await fetch(`${API_BASE_URL}/status-center/applications/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus, sendWhatsApp })
      });

      if (response.ok) {
        fetchApplications();
        fetchStats();
        setStatusDropdownOpen(null);
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
    setWhatsAppConfirm(null);
  };

  const handleVisaUpload = async () => {
    if (!visaFile || !visaUploadAppId) return;
    try {
      setUploadingVisa(true);
      const formData = new FormData();
      formData.append('visa', visaFile);

      const response = await fetch(`${API_BASE_URL}/gateway/applications/${visaUploadAppId}/upload-visa`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to upload visa');
      }

      setVisaUploadSuccess(true);
      setVisaFile(null);

      setApplications(prev => prev.map(app =>
        app.id === visaUploadAppId ? { ...app, visaUploaded: true } : app
      ));

      setTimeout(() => {
        setVisaUploadAppId(null);
        setVisaUploadSuccess(false);
      }, 2000);
    } catch (err) {
      console.error('Visa upload error:', err);
      alert('Failed to upload visa: ' + err.message);
    } finally {
      setUploadingVisa(false);
    }
  };

  // Status configurations
  const statusOptions = [
    { value: 'submitted', label: 'PENDING', className: 'status-pending' },
    { value: 'in_review', label: 'IN REVIEW', className: 'status-in-review' },
    { value: 'documents_required', label: 'DOCS REQUIRED', className: 'status-docs-required' },
    { value: 'processing', label: 'PROCESSING', className: 'status-processing' },
    { value: 'approved', label: 'APPROVED', className: 'status-approved' },
    { value: 'rejected', label: 'REJECTED', className: 'status-rejected' }
  ];

  const filterOptions = [
    { value: '', label: 'All Status' },
    { value: 'submitted', label: 'Pending' },
    { value: 'in_review', label: 'In Review' },
    { value: 'documents_required', label: 'Docs Required' },
    { value: 'processing', label: 'Processing' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' }
  ];

  const getStatusConfig = (status) => {
    return statusOptions.find(opt => opt.value === status) || statusOptions[0];
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (hours < 1) return 'JUST NOW';
    if (hours < 24) return `${hours}H AGO`;
    return `${days}D AGO`;
  };

  return (
    <div className="command-layout">
      <Sidebar />

      <main className="command-main">
        {/* Header */}
        <div className="command-header">
          <div className="header-left">
            <h1 className="command-title">COMMAND CENTER</h1>
            <p className="command-subtitle">REAL-TIME OPERATIONS DASHBOARD</p>
          </div>
          <div className="header-right">
            <div className="live-indicator">
              <span className="live-dot"></span>
              <span className="live-text">LIVE</span>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="stats-row">
          <div className="stat-card stat-primary">
            <div className="stat-header">
              <div className="stat-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
              </div>
              <div className="stat-trend up">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                </svg>
              </div>
            </div>
            <div className="stat-content">
              <span className="stat-label">PENDING PROCESSING</span>
              <span className="stat-value">{stats.pendingProcessing}</span>
            </div>
            <div className="stat-footer">
              <span className="stat-indicator-dot primary"></span>
              <span className="stat-indicator-text">REAL-TIME METRICS</span>
            </div>
          </div>

          <div className="stat-card stat-success">
            <div className="stat-header">
              <div className="stat-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2"/>
                  <path d="M3 9h18"/>
                  <path d="M9 21V9"/>
                </svg>
              </div>
              <div className="stat-trend up">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                </svg>
              </div>
            </div>
            <div className="stat-content">
              <span className="stat-label">PROCESSED VAULTS</span>
              <span className="stat-value">{stats.processedVaults}</span>
            </div>
            <div className="stat-footer">
              <span className="stat-indicator-dot success"></span>
              <span className="stat-indicator-text">REAL-TIME METRICS</span>
            </div>
          </div>

          <div className="stat-card stat-warning">
            <div className="stat-header">
              <div className="stat-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="1" x2="12" y2="23"/>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                </svg>
              </div>
              <div className="stat-trend up">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                </svg>
              </div>
            </div>
            <div className="stat-content">
              <span className="stat-label">
                {revenueFilter === 'month' ? 'THIS MONTH REVENUE' : 'ALL TIME REVENUE'}
              </span>
              <span className="stat-value">
                ${revenueFilter === 'month'
                  ? stats.currentMonthRevenue?.toLocaleString() || 0
                  : stats.allTimeRevenue?.toLocaleString() || 0}
              </span>
            </div>
            <div className="stat-footer">
              <div className="revenue-toggle">
                <button
                  className={`toggle-btn ${revenueFilter === 'month' ? 'active' : ''}`}
                  onClick={() => setRevenueFilter('month')}
                >
                  MONTH
                </button>
                <button
                  className={`toggle-btn ${revenueFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setRevenueFilter('all')}
                >
                  ALL TIME
                </button>
              </div>
            </div>
          </div>

          <div className="stat-card stat-error">
            <div className="stat-header">
              <div className="stat-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              </div>
              {stats.attentionNeeded > 0 && (
                <div className="stat-badge">{stats.attentionNeeded}</div>
              )}
            </div>
            <div className="stat-content">
              <span className="stat-label">ATTENTION NEEDED</span>
              <span className="stat-value">{stats.attentionNeeded}</span>
            </div>
            <div className="stat-footer">
              <span className="stat-indicator-dot error"></span>
              <span className="stat-indicator-text">REAL-TIME METRICS</span>
            </div>
          </div>
        </div>

        {/* Gateway Drawer */}
        {selectedAppId && (
          <div className="gateway-drawer-overlay" onClick={() => setSelectedAppId(null)}>
            <div className="gateway-drawer" onClick={(e) => e.stopPropagation()}>
              <div className="gateway-drawer-header">
                <button
                  className="gateway-drawer-close"
                  onClick={() => setSelectedAppId(null)}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
                <span className="gateway-drawer-app-id">
                  {applications.find(a => a.id === selectedAppId)?.applicationNumber || selectedAppId}
                </span>
              </div>
              <div className="gateway-drawer-body">
                <GatewaySession
                  applicationId={selectedAppId}
                  onClose={() => setSelectedAppId(null)}
                />
              </div>
            </div>
          </div>
        )}

            {/* Search and Filter Bar */}
            <div className="toolbar">
              <div className="search-box">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
                <input
                  type="text"
                  placeholder="Search by application ID or applicant..."
                  value={filters.search}
                  onChange={handleSearch}
                />
              </div>
              <div className="custom-dropdown" ref={filterDropdownRef}>
                <button
                  className="custom-dropdown-trigger"
                  onClick={() => setFilterDropdownOpen(!filterDropdownOpen)}
                >
                  <span>{filterOptions.find(opt => opt.value === filters.status)?.label || 'All Status'}</span>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                {filterDropdownOpen && (
                  <div className="custom-dropdown-menu">
                    {filterOptions.map((option) => (
                      <div
                        key={option.value}
                        className={`custom-dropdown-item ${filters.status === option.value ? 'active' : ''}`}
                        onClick={() => {
                          handleStatusFilter(option.value);
                          setFilterDropdownOpen(false);
                        }}
                      >
                        {option.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {filterData.visaTypes.length > 0 && (
                <div className="custom-dropdown" ref={visaTypeDropdownRef}>
                  <button
                    className="custom-dropdown-trigger"
                    onClick={() => setVisaTypeDropdownOpen(!visaTypeDropdownOpen)}
                  >
                    <span>{filters.visaType || 'All Visa Types'}</span>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  {visaTypeDropdownOpen && (
                    <div className="custom-dropdown-menu">
                      <div
                        className={`custom-dropdown-item ${!filters.visaType ? 'active' : ''}`}
                        onClick={() => { setFilters(prev => ({ ...prev, visaType: '', page: 1 })); setVisaTypeDropdownOpen(false); }}
                      >
                        All Visa Types
                      </div>
                      {filterData.visaTypes.map((vt) => (
                        <div
                          key={vt}
                          className={`custom-dropdown-item ${filters.visaType === vt ? 'active' : ''}`}
                          onClick={() => { setFilters(prev => ({ ...prev, visaType: vt, page: 1 })); setVisaTypeDropdownOpen(false); }}
                        >
                          {vt}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {filterData.countries.length > 0 && (
                <div className="custom-dropdown" ref={countryDropdownRef}>
                  <button
                    className="custom-dropdown-trigger"
                    onClick={() => setCountryDropdownOpen(!countryDropdownOpen)}
                  >
                    <span>{filters.country || 'All Countries'}</span>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  {countryDropdownOpen && (
                    <div className="custom-dropdown-menu">
                      <div
                        className={`custom-dropdown-item ${!filters.country ? 'active' : ''}`}
                        onClick={() => { setFilters(prev => ({ ...prev, country: '', page: 1 })); setCountryDropdownOpen(false); }}
                      >
                        All Countries
                      </div>
                      {filterData.countries.map((c) => (
                        <div
                          key={c}
                          className={`custom-dropdown-item ${filters.country === c ? 'active' : ''}`}
                          onClick={() => { setFilters(prev => ({ ...prev, country: c, page: 1 })); setCountryDropdownOpen(false); }}
                        >
                          {c}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Applications Table */}
            <div className="content-section">
              {appLoading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading applications...</p>
                </div>
              ) : applications.length === 0 ? (
                <div className="empty-state">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
                  </svg>
                  <p>No applications found</p>
                  <span className="empty-subtitle">Applications will appear here once submitted</span>
                </div>
              ) : (
                <>
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>APPLICANT & VAULT ID</th>
                          <th>VISA TYPE</th>
                          <th>PROCESSING STATUS</th>
                          <th>ACTIONS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {applications.map((app) => (
                          <tr key={app.id}>
                            <td>
                              <div className="applicant-info">
                                <div className="applicant-avatar">
                                  {app.applicant?.name?.charAt(0)?.toUpperCase() || 'A'}
                                </div>
                                <div className="applicant-details">
                                  <span className="applicant-name">{app.applicant?.name || 'Unknown'}</span>
                                  <span className="applicant-id">{app.applicationNumber}</span>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="visa-info">
                                <span className="visa-name">
                                  {app.visa?.name || `${app.route?.destination?.name} Visa`}
                                </span>
                                <span className="visa-details">
                                  {app.route?.destination?.flag} {app.route?.destination?.name} • {app.visa?.category || 'VISA'}
                                </span>
                              </div>
                            </td>
                            <td>
                              <div className="status-cell">
                                <div
                                  className="status-custom-dropdown"
                                  ref={el => statusDropdownRefs.current[app.id] = el}
                                >
                                  <button
                                    className={`status-dropdown-trigger ${getStatusConfig(app.status).className}`}
                                    onClick={(e) => {
                                      if (statusDropdownOpen === app.id) {
                                        setStatusDropdownOpen(null);
                                      } else {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        const spaceBelow = window.innerHeight - rect.bottom;
                                        const menuHeight = statusOptions.length * 32 + 8;
                                        const openUpward = spaceBelow < menuHeight;
                                        setDropdownPosition({
                                          top: openUpward ? rect.top - menuHeight : rect.bottom + 4,
                                          left: rect.left
                                        });
                                        setStatusDropdownOpen(app.id);
                                      }
                                    }}
                                  >
                                    <span>{getStatusConfig(app.status).label}</span>
                                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                                      <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  </button>
                                  {statusDropdownOpen === app.id && (
                                    <div
                                      className="status-dropdown-menu"
                                      style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
                                    >
                                      {statusOptions.map((option) => (
                                        <div
                                          key={option.value}
                                          className={`status-dropdown-item ${app.status === option.value ? 'active' : ''}`}
                                          onClick={() => requestStatusChange(app.id, option.value)}
                                        >
                                          {option.label}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <span className="status-time">UPDATED {formatDate(app.updatedAt)}</span>
                              </div>
                            </td>
                            <td>
                              <div className="actions-cell">
                                {app.status === 'approved' && !app.visaUploaded && (
                                  <button
                                    className="btn-upload-visa"
                                    onClick={() => setVisaUploadAppId(app.id)}
                                  >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                      <polyline points="17 8 12 3 7 8"/>
                                      <line x1="12" y1="3" x2="12" y2="15"/>
                                    </svg>
                                    UPLOAD VISA
                                  </button>
                                )}
                                {app.status === 'approved' && app.visaUploaded && (
                                  <span className="visa-sent-badge">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                                      <polyline points="22 4 12 14.01 9 11.01"/>
                                    </svg>
                                    VISA SENT
                                  </span>
                                )}
                                <button
                                  className="btn-check-status"
                                  onClick={() => setSelectedAppId(app.id)}
                                >
                                  MANAGE
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {pagination.totalPages > 1 && (
                    <div className="table-footer">
                      <div className="pagination-info">
                        Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                        {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                        {pagination.total} applications
                      </div>
                      <div className="pagination-controls">
                        <button
                          className="pagination-btn"
                          disabled={pagination.page === 1}
                          onClick={() => handlePageChange(pagination.page - 1)}
                        >
                          Previous
                        </button>
                        {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                          let pageNum;
                          if (pagination.totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (pagination.page <= 3) {
                            pageNum = i + 1;
                          } else if (pagination.page >= pagination.totalPages - 2) {
                            pageNum = pagination.totalPages - 4 + i;
                          } else {
                            pageNum = pagination.page - 2 + i;
                          }
                          return (
                            <button
                              key={pageNum}
                              className={`pagination-btn ${pagination.page === pageNum ? 'active' : ''}`}
                              onClick={() => handlePageChange(pageNum)}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                        <button
                          className="pagination-btn"
                          disabled={pagination.page === pagination.totalPages}
                          onClick={() => handlePageChange(pagination.page + 1)}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Status Change Confirmation Modal */}
            {statusConfirm && (
              <div className="wa-confirm-overlay" onClick={() => setStatusConfirm(null)}>
                <div className="wa-confirm-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="wa-confirm-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                      <path d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="#D97706" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3 className="wa-confirm-title">Change Application Status?</h3>
                  <p className="wa-confirm-text">
                    Change <strong>{statusConfirm.appNumber}</strong> from <strong>{getStatusConfig(statusConfirm.currentStatus).label}</strong> to <strong>{getStatusConfig(statusConfirm.newStatus).label}</strong>?
                  </p>
                  <div className="wa-confirm-actions">
                    <button className="wa-confirm-skip" onClick={() => setStatusConfirm(null)}>
                      Cancel
                    </button>
                    <button className="wa-confirm-send" style={{ background: '#2188C9' }} onClick={confirmStatusChange}>
                      Confirm
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* WhatsApp Confirmation Modal */}
            {whatsAppConfirm && (
              <div className="wa-confirm-overlay" onClick={() => {
                executeStatusChange(whatsAppConfirm.appId, whatsAppConfirm.newStatus, false);
              }}>
                <div className="wa-confirm-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="wa-confirm-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" fill="#25D366"/>
                      <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2z" stroke="#25D366" strokeWidth="1.5" fill="none"/>
                    </svg>
                  </div>
                  <h3 className="wa-confirm-title">Send WhatsApp Notification?</h3>
                  <p className="wa-confirm-text">
                    Notify <strong>{whatsAppConfirm.applicantName}</strong> about visa {whatsAppConfirm.newStatus === 'approved' ? 'approval' : 'rejection'} via WhatsApp?
                  </p>
                  <div className="wa-confirm-actions">
                    <button
                      className="wa-confirm-skip"
                      onClick={() => executeStatusChange(whatsAppConfirm.appId, whatsAppConfirm.newStatus, false)}
                    >
                      No, Skip
                    </button>
                    <button
                      className="wa-confirm-send"
                      onClick={() => executeStatusChange(whatsAppConfirm.appId, whatsAppConfirm.newStatus, true)}
                    >
                      Yes, Send
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Visa Upload Modal */}
            {visaUploadAppId && (
              <div className="visa-modal-overlay" onClick={() => !uploadingVisa && (setVisaUploadAppId(null), setVisaFile(null), setVisaUploadSuccess(false))}>
                <div className="visa-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="visa-modal-header">
                    <h3>Upload Visa Document</h3>
                    <button
                      className="visa-modal-close"
                      onClick={() => !uploadingVisa && (setVisaUploadAppId(null), setVisaFile(null), setVisaUploadSuccess(false))}
                      disabled={uploadingVisa}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  </div>
                  <div className="visa-modal-body">
                    {visaUploadSuccess ? (
                      <div className="visa-upload-success">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                          <polyline points="22 4 12 14.01 9 11.01"/>
                        </svg>
                        <p>Visa uploaded successfully!</p>
                        <span>Available in applicant's vault</span>
                      </div>
                    ) : (
                      <>
                        <p>Upload the approved visa document. It will be stored in the applicant's vault with OCR extraction.</p>
                        <div className="visa-modal-input-group">
                          <label>Visa Document (PDF, JPEG, PNG)</label>
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png,.webp"
                            onChange={(e) => setVisaFile(e.target.files[0] || null)}
                            disabled={uploadingVisa}
                          />
                          {visaFile && (
                            <span className="visa-file-info">{visaFile.name} ({(visaFile.size / 1024).toFixed(0)} KB)</span>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  {!visaUploadSuccess && (
                    <div className="visa-modal-footer">
                      <button
                        className="visa-modal-cancel"
                        onClick={() => { setVisaUploadAppId(null); setVisaFile(null); }}
                        disabled={uploadingVisa}
                      >
                        Cancel
                      </button>
                      <button
                        className="visa-modal-upload"
                        onClick={handleVisaUpload}
                        disabled={uploadingVisa || !visaFile}
                      >
                        {uploadingVisa ? 'Uploading...' : 'Upload Visa'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
      </main>
    </div>
  );
};

export default CommandCenter;
